import { DayWorkingHours } from '../infra/adapters/luminara/calendar/dto/common/day-working-hours.dto';
import { WorkingHourEntry } from '../infra/adapters/luminara/calendar/dto/common/working-hour-entry.dto';

export const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
export const dateTimeOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', hour: 'numeric', minute: 'numeric', hour12: true };

export const calculateTimeDifference = (time1: Date, time2: Date) => {
  return time1.getTime() - time2.getTime();
};

export const getCurrentTime = () => new Date();

export const convertToDate = (date: string | Date) => {
  return typeof date === 'string' ? new Date(date) : date;
};

export const minutesToMilliseconds = (minutes: number) => minutes * 60 * 1000;
export const secondsToMilliseconds = (seconds: number) => seconds * 1000;
export const milisecondsToMinutesRounded = (miliseconds: number) => Math.floor(miliseconds / 1000 / 60);
export const milisecondsToSecondsRounded = (miliseconds: number) => Math.floor(miliseconds / 1000);

export const convertMilisecondsToMinutesAndSecondsObject = (miliseconds: number): { minutes: number; seconds: number } => {
  const minutes = milisecondsToMinutesRounded(miliseconds);
  const seconds = milisecondsToSecondsRounded(miliseconds - minutesToMilliseconds(minutes));
  return {
    minutes,
    seconds,
  };
};

export const formatDateToReadableString = (date: Date) => {
  const dateString = date.toLocaleString('en-US', dateTimeOptions).replace(',', ' at');

  const day = date.getDate();
  let suffix = 'th';

  if (day < 11 || day > 20) {
    switch (day % 10) {
      case 1:
        suffix = 'st';
        break;
      case 2:
        suffix = 'nd';
        break;
      case 3:
        suffix = 'rd';
        break;
    }
  }

  return dateString.replace(/(\d+)/, `$1${suffix}`);
};

export const getPrettyDateTime = (date?: string) => {
  return date ? formatDateToReadableString(new Date(date)) : undefined;
};

export const getPrettyTime = (date?: string) => {
  return date !== undefined ? getPrettyDateTime(date)?.split(' at ')[1] : undefined;
};

export const getPrettyDate = (date?: string) => {
  return date ? getPrettyDateTime(date)?.split(' at ')[0] : undefined;
};

export const stringifyTime = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export function calculateTotalHoursForWeek(availability: DayWorkingHours[]): number {
  let totalHoursForWeek = 0;

  availability.forEach((day) => {
    if (day.enabled) {
      const occupiedHours: Set<number> = new Set();
      totalHoursForWeek += calculateDayWorkingHours(day.workingHoursEntries, occupiedHours);
    }
  });

  return totalHoursForWeek;
}

function calculateDayWorkingHours(workingHoursEntries: WorkingHourEntry[], occupiedHours: Set<number>): number {
  let totalHoursForDay = 0;

  workingHoursEntries.forEach((entry) => {
    if (entry.start && entry.end) {
      const { totalStart, totalEnd } = calculateTotalHours(entry.start, entry.end);
      totalHoursForDay += markOccupiedHours(totalStart, totalEnd, occupiedHours);
    }
  });

  return totalHoursForDay;
}

function calculateTotalHours(start: string, end: string): { totalStart: number; totalEnd: number } {
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);

  const totalStart = startHour + startMinute / 60;
  const totalEnd = endHour + endMinute / 60;

  return { totalStart, totalEnd };
}

function markOccupiedHours(totalStart: number, totalEnd: number, occupiedHours: Set<number>): number {
  let totalHours = 0;

  for (let i = totalStart; i < totalEnd; i += 0.25) {
    if (!occupiedHours.has(i)) {
      occupiedHours.add(i);
      totalHours += 0.25;
    }
  }

  return totalHours;
}

export const isMoreThanXHoursAway = (date: Date, hoursAway: number): boolean => {
  const currentTime = new Date();
  const differenceInMilliseconds = date.getTime() - currentTime.getTime();
  const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
  return differenceInHours > hoursAway;
};

export const calculateMinuteDifference = (date1: Date | string | undefined, date2: Date | string | undefined): number => {
  return calculateSecondsDifference(date1, date2) / 60;
};

export const calculateSecondsDifference = (date1: Date | string | undefined, date2: Date | string | undefined): number => {
  if (date1 == null || date2 == null) {
    return 0;
  }
  date1 = convertToDate(date1);
  date2 = convertToDate(date2);
  const differenceMs = date2.getTime() - date1.getTime();
  const differenceMinutes = Math.round(differenceMs / 1000);
  return differenceMinutes;
};

export const convertToAddToCalendarObject = (bookedTime?: string, endTime?: string): AddToCalendarProps | undefined => {
  if (bookedTime == null || endTime == null) {
    return undefined;
  } else {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const sessionStartDate = bookedTime!.split('T')[0];
    const sessionStartTime = new Date(bookedTime!).toLocaleTimeString([], timeOptions);
    const sessionEndTime = new Date(endTime!).toLocaleTimeString([], timeOptions);

    return {
      sessionStartDate,
      sessionStartTime,
      sessionEndTime,
      timezone,
    };
  }
};

export interface AddToCalendarProps {
  sessionStartDate: string;
  sessionStartTime: string;
  sessionEndTime: string;
  timezone: string;
}

export const ONE_MINUTE = minutesToMilliseconds(1);
export const TEN_SECONDS = secondsToMilliseconds(10);
export const FIVE_SECONDS = secondsToMilliseconds(5);
export const THREE_SECONDS = secondsToMilliseconds(3);
export const TWO_SECONDS = secondsToMilliseconds(2);
export const ONE_SECOND = secondsToMilliseconds(1);
